import { Injectable } from "@angular/core";
import { AuthenticationDataService } from "@omni/data-services/authentication/authentication.service";
import { distinctUntilChanged, firstValueFrom, map, Observable } from "rxjs";
import { AuthenticationService } from "../authentication.service";
import { IoConfigurationFeatureCodes } from "@omni/classes/authentication/user.class";

/**
 * Granularity of an Azure Maps postal code boundary. Which one a market is
 * covered at varies, so a boundary lookup tries them in order.
 */
export type PostalCodeResultType = 'postalCode' | 'postalCode2';

@Injectable({
  providedIn: 'root'
})
export class AzureMapsService {
  readonly clientId$: Observable<string>;
  constructor(
    private readonly authDataService: AuthenticationDataService,
    private readonly authenticationService: AuthenticationService,
  ) {
    this.clientId$ = this.authenticationService.user$.pipe(
      map(user => user.ioConfigurations.find(c => c.configName === IoConfigurationFeatureCodes.AZURE_MAPS_CLIENT_ID)?.configValue),
      map(clientId => {
        if (!clientId) {
          throw new Error('Azure Maps client ID is not set');
        }
        return clientId;
      }),
      distinctUntilChanged(),
    );
  }

  async getToken() {
    const token = await this.authDataService.getToken('https://atlas.microsoft.com/.default');
    return token.token;
  }

  async #getHeaders() {
    return {
      headers: {
        'Authorization': `Bearer ${await this.getToken()}`,
        'x-ms-client-id': await firstValueFrom(this.clientId$),
      },
    };
  }

  async #fetch(url: string, searchParams: URLSearchParams) {
    const response = await fetch(`${url}?${searchParams.toString()}`, {
      ...await this.#getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async geocode(postalCode: string, countryRegion: string) {
    const url = 'https://atlas.microsoft.com/geocode'
    const searchParams = new URLSearchParams({
      'api-version': '2023-06-01',
      top: '1',
      countryRegion: countryRegion,
      postalCode: postalCode,
    });
    const data = await this.#fetch(url, searchParams);
    return data.features?.[0]?.geometry?.coordinates as [number, number];
  }

  *#getPolygons(geojson: any): Generator<[number, number][][]> {
    if (!geojson) return;
    let array;
    switch (geojson.type) {
      case 'Polygon':
        // coordinates: [ [ [x,y], ... ] ]
        yield geojson.coordinates;
        break;

      case 'MultiPolygon':
        // coordinates: [ [ [ [x,y], ... ] ] ]
        yield* geojson.coordinates;
        break;

      case 'Feature':
        yield* this.#getPolygons(geojson.geometry);
        break;

      case 'FeatureCollection':
        array = geojson.features;
        break;

      case 'GeometryCollection':
        array = geojson.geometries;
        break;
    }
    if (!array) return;
    for (const item of array) {
      yield* this.#getPolygons(item);
    }
  }

  /**
   * Retrieves the boundary Azure Maps holds at `coordinates` for the given
   * result type, along with the name of the area it resolved to. Callers that
   * requested a specific area should compare that name before using the
   * geometry: the boundary returned is the one containing the coordinates,
   * which can be an adjacent area.
   *
   * @throws when Azure Maps holds no boundary of that result type at the
   * coordinates, which varies by market.
   */
  async searchPolygon(coordinates: [number, number], resultType: PostalCodeResultType = 'postalCode') {
    if (!coordinates?.length) {
      return { name: null, polygons: [] as [number, number][][][] };
    }
    const url = 'https://atlas.microsoft.com/search/polygon'
    const searchParams = new URLSearchParams({
      'api-version': '2025-01-01',
      'resultType': resultType,
      coordinates: coordinates.join(','),
    });
    const data = await this.#fetch(url, searchParams);
    return {
      name: (data.properties?.name ?? null) as string | null,
      polygons: [...this.#getPolygons(data.geometry)],
    };
  }
}
